import React, { useState, useEffect } from "react";
import { Modal, Button, Progress, Typography } from "antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { hayTrabajoEnCurso, trabajosEnCurso } from "../../services/trabajoEnCurso";

const { Title, Text } = Typography;

const CriticalErrorModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [countdownActive, setCountdownActive] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  // Qué había a medias cuando saltó el error. Se congela en ese instante: la
  // cuenta atrás decide una sola vez si corre o no, y no se pone a correr sola
  // porque el cajero cierre la venta mientras lee el aviso.
  const [aMedias, setAMedias] = useState([]);

  useEffect(() => {
    const handleCriticalError = (event) => {
      // Evitar múltiples modales - solo mostrar uno por sesión
      if (modalShown) {
        return;
      }

      const { operationName, maxRetries, error } = event.detail;

      setErrorInfo({
        operationName,
        maxRetries,
        error: error?.message || "Error desconocido",
      });
      // Con una venta a medias no se reinicia solo. Un reinicio automático se
      // lleva lo que el cajero no ha confirmado, y el error de sincronización
      // que lo dispara no tiene por qué costarle la venta que tiene delante.
      const pendiente = hayTrabajoEnCurso() ? trabajosEnCurso() : [];
      setAMedias(pendiente);

      setIsVisible(true);
      setCountdown(30);
      setCountdownActive(pendiente.length === 0);
      setModalShown(true);

      // Reproducir sonido de alerta si está disponible
      try {
        const audio = new Audio(
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUX7zq66hVFApGn+DyvmwhBy2J0+/ejTsBFWSm7dWVdVYDZjXTfnwDHRLX3wjGQNBBCNLw8YYGUDGBPvJyJhTUZKM8BQjdWRs0HEcJCVgA=="
        );
        audio.play().catch(() => {
          // Ignorar error si no se puede reproducir
        });
      } catch (error) {
        // Ignorar error de audio
      }
    };

    window.addEventListener("criticalSyncError", handleCriticalError);

    return () => {
      window.removeEventListener("criticalSyncError", handleCriticalError);
    };
  }, [modalShown]);

  useEffect(() => {
    let timer;
    if (countdownActive && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Tiempo agotado, recargar página
            window.location.reload();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdownActive, countdown]);

  const handleReloadNow = () => {
    if (aMedias.length === 0) {
      window.location.reload();
      return;
    }
    // Reiniciar acá es explícito, pero igual se pierde lo que hay a medias, así
    // que se dice antes y con la palabra que toca.
    window.messageApi.confirm({
      title: "¿Reiniciar y perder lo que está a medias?",
      content: `Se perderá ${aMedias.join(" y ")}. Esta acción no se puede deshacer.`,
      okText: "Reiniciar de todos modos",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => window.location.reload(),
    });
  };

  const handleCancel = () => {
    setIsVisible(false);
    setCountdownActive(false);
    setCountdown(30);
    // Resetear el flag para permitir futuros modales si es necesario
    setModalShown(false);
  };

  if (!isVisible || !errorInfo) {
    return null;
  }

  const progressPercent = ((30 - countdown) / 30) * 100;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: "20px" }} />
          <span>Error crítico de sincronización</span>
        </div>
      }
      open={isVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancelar
        </Button>,
        <Button
          key="reload"
          type="primary"
          danger
          icon={<ReloadOutlined />}
          onClick={handleReloadNow}
        >
          Reiniciar ahora
        </Button>,
      ]}
      width={500}
      centered
      maskClosable={false}
      closable={true}
    >
      <div style={{ padding: "20px 0" }}>
        <Title level={4} style={{ color: "#ff4d4f", marginBottom: "16px" }}>
          La sincronización ha fallado {errorInfo.maxRetries} veces
        </Title>

        <div style={{ marginBottom: "20px" }}>
          <Text strong>Operación que falló:</Text>
          <br />
          <Text>{errorInfo.operationName}</Text>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <Text strong>Error:</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {errorInfo.error}
          </Text>
        </div>

        {aMedias.length > 0 ? (
          <div
            style={{
              background: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: "6px",
              padding: "16px",
              marginBottom: "20px",
            }}
          >
            <Text strong style={{ color: "#ad6800" }}>
              El terminal no se reiniciará solo
            </Text>
            <br />
            <Text style={{ color: "#ad6800" }}>
              Tienes {aMedias.join(" y ")}. Termina lo que estás haciendo y reinicia cuando puedas;
              si reinicias ahora, se pierde.
            </Text>
          </div>
        ) : (
          <div
            style={{
              background: "#fff2f0",
              border: "1px solid #ffccc7",
              borderRadius: "6px",
              padding: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ marginBottom: "12px" }}>
              <Text strong style={{ color: "#cf1322" }}>
                La aplicación se reiniciará automáticamente en:
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Progress
                type="circle"
                size={60}
                percent={progressPercent}
                format={() => (
                  <span style={{ color: "#cf1322", fontWeight: "bold" }}>{countdown}s</span>
                )}
                strokeColor="#ff4d4f"
              />
              <Text style={{ color: "#cf1322" }}>
                Esto puede ayudar a resolver problemas de conectividad
                <br />o estado de la aplicación.
              </Text>
            </div>
          </div>
        )}

        <div
          style={{
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: "6px",
            padding: "12px",
          }}
        >
          <Text style={{ color: "#389e0d", fontSize: "12px" }}>
            💡 <strong>Sugerencia:</strong> Verifica tu conexión a internet antes de continuar.
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default CriticalErrorModal;
